/**
 * MobileWeekView - Mobile-optimized calendar view (Jan 15, 2026)
 * 
 * Features:
 * - Shows 1 day at a time with swipe/tap navigation
 * - Larger touch targets for slots
 * - Simplified slot display
 * - Bottom navigation for day switching
 * - Touch-friendly booking interactions
 */
import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { 
  formatDate, 
  formatTime,
  getAppointmentsForSlot, 
  getAvailabilityForSlot, 
  isSlotBookableMultipleServers,
  calculateFloatingStartTime
} from '../../../utils/calendarHelpers';

/**
 * Get the current date/time in a specific timezone
 */
const getCurrentTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    const timeStr = now.toLocaleString('en-US', { 
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const match = timeStr.match(/(\d+):(\d+)/);
    if (match) {
      return {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
        dateStr: now.toLocaleDateString('en-CA', { timeZone: timezone })
      };
    }
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  } catch (e) {
    const now = new Date();
    return { hours: now.getHours(), minutes: now.getMinutes(), dateStr: formatDate(now) };
  }
};

const MobileWeekView = ({
  weekDays,
  timeSlots,
  appointments,
  slotOccupancy = { booked_slots: [], held_slots: [] },
  availability,
  calendar,
  services,
  servers,
  user,
  isAdmin,
  isCustomer,
  setSelectedSlot,
  setSelectedAvailability,
  setShowAppointmentModal,
  setSelectedAppointment,
  setShowEditAppointmentModal,
  setContextMenu,
  token,
  getAuthHeaders,
  initialDate  // BOOKING DATE NAVIGATION (Jan 15, 2026): Optional date to navigate to
}) => {
  // Track which day is currently being viewed (index into weekDays)
  const [currentDayIndex, setCurrentDayIndex] = useState(() => {
    const calendarTimezone = calendar?.timezone || 'UTC';
    
    // BOOKING DATE NAVIGATION (Jan 15, 2026):
    // If initialDate is provided (from confirmation email), navigate to that date
    if (initialDate) {
      const targetIndex = weekDays.findIndex(day => formatDate(day) === initialDate);
      if (targetIndex >= 0) {
        return targetIndex;
      }
    }
    
    // Default to today if it's in the week, otherwise first day
    const tzTime = getCurrentTimeInTimezone(calendarTimezone);
    const todayIndex = weekDays.findIndex(day => formatDate(day) === tzTime.dateStr);
    return todayIndex >= 0 ? todayIndex : 0;
  });
  
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  
  const currentDay = weekDays[currentDayIndex];
  const calendarTimezone = calendar?.timezone || 'UTC';
  const tzTime = getCurrentTimeInTimezone(calendarTimezone);
  const isToday = formatDate(currentDay) === tzTime.dateStr;
  const dayOfWeek = currentDay.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  // Swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0 && currentDayIndex < weekDays.length - 1) {
        // Swipe left - next day
        setCurrentDayIndex(prev => prev + 1);
      } else if (diff < 0 && currentDayIndex > 0) {
        // Swipe right - previous day
        setCurrentDayIndex(prev => prev - 1);
      }
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const goToPrevDay = () => {
    if (currentDayIndex > 0) {
      setCurrentDayIndex(prev => prev - 1);
    }
  };

  const goToNextDay = () => {
    if (currentDayIndex < weekDays.length - 1) {
      setCurrentDayIndex(prev => prev + 1);
    }
  };

  const goToToday = () => {
    const todayIndex = weekDays.findIndex(day => formatDate(day) === tzTime.dateStr);
    if (todayIndex >= 0) {
      setCurrentDayIndex(todayIndex);
    }
  };

  // Get header styling
  let headerBgClass = 'bg-gray-100';
  let headerTextClass = 'text-gray-800';
  
  if (isToday) {
    headerBgClass = 'bg-blue-100';
    headerTextClass = 'text-blue-700';
  } else if (isWeekend) {
    headerBgClass = 'bg-red-50';
    headerTextClass = 'text-red-600';
  }

  return (
    <div 
      className="flex flex-col h-full bg-white"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Day Header with Navigation */}
      <div className={`sticky top-0 z-20 ${headerBgClass} border-b shadow-sm`}>
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={goToPrevDay}
            disabled={currentDayIndex === 0}
            className={`p-2 rounded-full ${
              currentDayIndex === 0 
                ? 'text-gray-300' 
                : 'text-gray-600 hover:bg-gray-200 active:bg-gray-300'
            }`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <div className="text-center">
            <div className={`text-lg font-bold ${headerTextClass}`}>
              {currentDay.toLocaleDateString('en-US', { weekday: 'long' })}
            </div>
            <div className={`text-2xl font-bold ${headerTextClass}`}>
              {currentDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            {isToday && (
              <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                Today
              </span>
            )}
          </div>
          
          <button
            onClick={goToNextDay}
            disabled={currentDayIndex === weekDays.length - 1}
            className={`p-2 rounded-full ${
              currentDayIndex === weekDays.length - 1 
                ? 'text-gray-300' 
                : 'text-gray-600 hover:bg-gray-200 active:bg-gray-300'
            }`}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
        
        {/* Day dots indicator */}
        <div className="flex justify-center gap-1.5 pb-2">
          {weekDays.map((day, idx) => {
            const dayDateStr = formatDate(day);
            const isDayToday = dayDateStr === tzTime.dateStr;
            return (
              <button
                key={idx}
                onClick={() => setCurrentDayIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentDayIndex 
                    ? isDayToday ? 'bg-blue-500 w-4' : 'bg-gray-700 w-4'
                    : isDayToday ? 'bg-blue-300' : 'bg-gray-300'
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Time Slots */}
      <div className="flex-1 overflow-y-auto">
        {timeSlots.map((hour) => {
          const slotAppointments = getAppointmentsForSlot(appointments, currentDay, hour);
          const slotAvailability = getAvailabilityForSlot(availability, currentDay, hour);
          const dayDateStr = formatDate(currentDay);
          
          const isTodayInCalendarTz = dayDateStr === tzTime.dateStr;
          const isCurrentHour = hour === tzTime.hours && isTodayInCalendarTz;
          const isPastSlot = (dayDateStr < tzTime.dateStr) || 
            (dayDateStr === tzTime.dateStr && hour < tzTime.hours);

          const hasMyAppointment = user?.role === 'customer' && 
            slotAppointments.some(apt => apt.customer_id === user.id);
          
          const bookabilityCheck = isSlotBookableMultipleServers(
            calendar, 
            availability, 
            appointments, 
            currentDay, 
            hour, 
            slotAvailability,
            slotOccupancy?.booked_slots || null,
            slotOccupancy?.held_slots || null,
            isCustomer && isCustomer()
          );
          const isBookable = bookabilityCheck.bookable && !isPastSlot;
          
          const isSlotBookedByOthers = isCustomer && isCustomer() && slotOccupancy?.booked_slots?.some(slot => {
            return slot.date === dayDateStr && slot.time === `${hour.toString().padStart(2, '0')}:00`;
          });

          // Determine slot styling
          let slotBgClass = 'bg-white';
          let slotBorderClass = 'border-gray-200';
          let statusText = '';
          let statusColor = 'text-gray-500';
          
          if (isPastSlot) {
            slotBgClass = 'bg-gray-100';
            statusText = 'Past';
            statusColor = 'text-gray-400';
          } else if (hasMyAppointment) {
            slotBgClass = 'bg-blue-50';
            slotBorderClass = 'border-blue-300';
          } else if (isSlotBookedByOthers) {
            slotBgClass = 'bg-gray-100';
            statusText = 'Booked';
            statusColor = 'text-gray-500';
          } else if (slotAvailability) {
            if (slotAvailability.is_on_hold) {
              slotBgClass = 'bg-orange-50';
              statusText = 'On Hold';
              statusColor = 'text-orange-600';
            } else if (isBookable) {
              slotBgClass = 'bg-green-50';
              slotBorderClass = 'border-green-200';
              statusText = 'Available';
              statusColor = 'text-green-600';
            }
          }

          return (
            <div 
              key={hour}
              className={`flex border-b ${slotBorderClass} ${slotBgClass} ${
                isCurrentHour ? 'ring-2 ring-blue-400 ring-inset' : ''
              }`}
            >
              {/* Time Label */}
              <div className={`w-16 flex-shrink-0 p-3 border-r bg-gray-50 text-sm font-medium ${
                isCurrentHour ? 'text-blue-600' : 'text-gray-600'
              }`}>
                {formatTime(hour)}
              </div>
              
              {/* Slot Content */}
              <div 
                className={`flex-1 p-3 min-h-[70px] ${
                  isBookable && !hasMyAppointment && !isSlotBookedByOthers
                    ? 'active:bg-green-100 cursor-pointer' 
                    : ''
                }`}
                onClick={() => {
                  if (isBookable && !isPastSlot && !hasMyAppointment && !isSlotBookedByOthers) {
                    const startTime = `${hour.toString().padStart(2, '0')}:00`;
                    setSelectedSlot({
                      date: formatDate(currentDay),
                      hour: hour,
                      time: startTime
                    });
                    setShowAppointmentModal(true);
                  }
                }}
              >
                {/* Show appointments */}
                {slotAppointments.length > 0 ? (
                  slotAppointments.map((apt, aptIdx) => {
                    const service = services.find(s => s.id === apt.service_id);
                    const serviceColor = service?.color_code || '#3B82F6';
                    const isMyAppointment = user?.role === 'customer' && apt.customer_id === user.id;
                    
                    return (
                      <div
                        key={aptIdx}
                        className={`border-l-4 p-2 rounded mb-2 ${
                          isPastSlot ? 'opacity-60' : ''
                        }`}
                        style={{
                          backgroundColor: isPastSlot ? '#E5E7EB' : serviceColor + '20',
                          borderLeftColor: isPastSlot ? '#9CA3AF' : serviceColor
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isMyAppointment && !isPastSlot) {
                            // Show cancel option for customer's own booking
                            setSelectedAppointment(apt);
                            setContextMenu({
                              x: e.clientX,
                              y: e.clientY,
                              isCustomerBooking: true
                            });
                          } else if (isAdmin && isAdmin() && !isPastSlot) {
                            setSelectedAppointment(apt);
                            setShowEditAppointmentModal(true);
                          }
                        }}
                      >
                        <div className="font-semibold text-sm" style={{ color: isPastSlot ? '#6B7280' : serviceColor }}>
                          {isAdmin && isAdmin() ? apt.customer_name : 'Your Booking'}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {apt.service_name}
                        </div>
                        {isMyAppointment && !isPastSlot && (
                          <div className="text-xs text-blue-500 mt-1">
                            Tap to cancel
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  /* Show status for empty slots */
                  statusText && (
                    <div className={`text-sm ${statusColor} flex items-center h-full`}>
                      {statusText}
                      {statusText === 'Available' && (
                        <span className="text-xs ml-2 text-gray-400">Tap to book</span>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Navigation Bar */}
      <div className="sticky bottom-0 bg-white border-t shadow-lg px-4 py-2">
        <div className="flex justify-around items-center">
          <button
            onClick={goToToday}
            className="flex flex-col items-center text-blue-600 active:text-blue-800"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs mt-1">Today</span>
          </button>
          
          {/* Mini week navigation */}
          <div className="flex gap-1">
            {weekDays.map((day, idx) => {
              const dayDateStr = formatDate(day);
              const isDayToday = dayDateStr === tzTime.dateStr;
              const isSelected = idx === currentDayIndex;
              const dayNum = day.getDate();
              const dayName = day.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
              
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentDayIndex(idx)}
                  className={`w-9 h-12 rounded-lg flex flex-col items-center justify-center text-xs ${
                    isSelected 
                      ? isDayToday ? 'bg-blue-500 text-white' : 'bg-gray-700 text-white'
                      : isDayToday ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="font-medium">{dayName}</span>
                  <span className="font-bold">{dayNum}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border-t px-4 py-2">
        <div className="flex justify-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-200 border border-gray-300 rounded"></div>
            <span>Booked</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
            <span>Your Booking</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileWeekView;
